
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { FoodItem } from '@/components/food-search/types';

interface CustomIngredient extends FoodItem {
  quantity: number;
  unit: string;
  displayQuantity?: string;
}

interface CustomIngredientsTableProps {
  customIngredients: CustomIngredient[];
  onRemoveCustomIngredient: (index: number) => void;
}

const CustomIngredientsTable: React.FC<CustomIngredientsTableProps> = ({
  customIngredients,
  onRemoveCustomIngredient
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          🥗 Alimentos Adicionales
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {customIngredients.map((ingredient, index) => {
            // Los valores nutricionales ya vienen calculados desde useFoodSearch
            // No necesitamos recalcular aquí
            const displayCalories = Math.round(ingredient.calories * 10) / 10;
            const displayProtein = Math.round(ingredient.protein * 10) / 10;
            const displayCarbs = Math.round(ingredient.carbs * 10) / 10;
            const displayFat = Math.round(ingredient.fat * 10) / 10;
            const displayIron = ingredient.iron ? Math.round(ingredient.iron * 10) / 10 : null;
            const displayCalcium = ingredient.calcium ? Math.round(ingredient.calcium * 10) / 10 : null;
            const displayZinc = ingredient.zinc ? Math.round(ingredient.zinc * 10) / 10 : null;
            const displayMagnesium = ingredient.magnesium ? Math.round(ingredient.magnesium * 10) / 10 : null;
            const displayOmega3 = ingredient.omega3 ? Math.round(ingredient.omega3 * 10) / 10 : null;

            return (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-4">
                    <span className="font-medium">{ingredient.name}</span>
                    <span className="text-sm text-gray-600">
                      {ingredient.displayQuantity || `${ingredient.quantity}${ingredient.unit}`}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 flex gap-4">
                    <span>{displayCalories} kcal</span>
                    <span>P: {displayProtein}g</span>
                    <span>C: {displayCarbs}g</span>
                    <span>G: {displayFat}g</span>
                    {displayIron && (
                      <span>Fe: {displayIron}mg</span>
                    )}
                    {displayCalcium && (
                      <span>Ca: {displayCalcium}mg</span>
                    )}
                    {displayZinc && (
                      <span>Zn: {displayZinc}mg</span>
                    )}
                    {displayMagnesium && (
                      <span>Mg: {displayMagnesium}mg</span>
                    )}
                    {displayOmega3 && (
                      <span>Ω3: {displayOmega3}g</span>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveCustomIngredient(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default CustomIngredientsTable;
